import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import LoginPage from "@/pages/LoginPage";
import OnboardPage from "@/pages/OnboardPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ManagerDashboard from "@/pages/ManagerDashboard";
import OperativeDashboard from "@/pages/OperativeDashboard";
import JobDetailPage from "@/pages/JobDetailPage";
import TeamPage from "@/pages/TeamPage";
import WeekSchedulePage from "@/pages/WeekSchedulePage";
import PipelinePage from "@/pages/PipelinePage";
import WorkInProgressPage from "@/pages/WorkInProgressPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import TimesheetsPage from "@/pages/TimesheetsPage";
import ChecklistLibraryPage from "@/pages/ChecklistLibraryPage";
import AppShell from "@/components/AppShell";
import { bindPushMessageListener } from "@/lib/push";

function LoadingSplash() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
            <div className="text-[var(--text-muted)] font-medium">Loading SCS…</div>
        </div>
    );
}

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading || user === null) return <LoadingSplash />;
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function ManagerOnly({ children }) {
    const { user } = useAuth();
    if (user?.role !== "manager") return <Navigate to="/operative" replace />;
    return children;
}

function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading || user === null) return <LoadingSplash />;
    if (!user) return <Navigate to="/login" replace />;
    return <Navigate to={user.role === "manager" ? "/manager" : "/operative"} replace />;
}

function App() {
    useEffect(() => { bindPushMessageListener(); }, []);
    return (
        <div className="App">
            <AuthProvider>
                <NotificationsProvider>
                    <BrowserRouter>
                        <Routes>
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                            <Route path="/onboard/:token" element={<OnboardPage />} />
                            <Route
                                element={
                                    <Protected>
                                        <AppShell />
                                    </Protected>
                                }
                            >
                                <Route path="/manager" element={<ManagerOnly><ManagerDashboard /></ManagerOnly>} />
                                <Route path="/schedule" element={<WeekSchedulePage />} />
                                <Route path="/work-in-progress" element={<WorkInProgressPage />} />
                                <Route path="/pipeline" element={<ManagerOnly><PipelinePage /></ManagerOnly>} />
                                <Route path="/clients" element={<ManagerOnly><ClientsPage /></ManagerOnly>} />
                                <Route path="/clients/:clientId" element={<ManagerOnly><ClientDetailPage /></ManagerOnly>} />
                                <Route path="/team" element={<ManagerOnly><TeamPage /></ManagerOnly>} />
                                <Route path="/timesheets" element={<ManagerOnly><TimesheetsPage /></ManagerOnly>} />
                                <Route path="/settings/checklist-library" element={<ManagerOnly><ChecklistLibraryPage /></ManagerOnly>} />
                                <Route path="/operative" element={<OperativeDashboard />} />
                                <Route path="/jobs/:jobId" element={<JobDetailPage />} />
                            </Route>
                            <Route path="/" element={<RootRedirect />} />
                            <Route path="*" element={<RootRedirect />} />
                        </Routes>
                        <Toaster richColors position="top-right" />
                    </BrowserRouter>
                </NotificationsProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
